import {Model, ModelProviderProps} from '@adobe/cq-react-editable-components';
import {ModelManager, TransferModel} from '@adobe/cq-spa-page-model-manager';
import * as React from 'react';
import {modelToProps} from '../api/modelToProps';
import {observer} from 'mobx-react-lite';
import {useModelStore} from '../../context';

const passableProps = ['cqPath', 'containerProps', 'forceReload', 'noElement'];

export interface ModelProviderState {
    model: Model | null;
}

export function mergeProps(model: any, props: any): any {
    const extraProps = props.extraProps;
    model = model || props;
    const newProps = {...model, ...extraProps};
    passableProps.forEach((key: string) => (newProps[key] = props[key]));
    delete newProps.extraProps;

    return newProps;
}

export function withModel(
    wrappedComponent: React.ComponentType<any>
): React.FunctionComponent<Model> {
    return (props: Model) => {
        const mpProps: ModelProviderProps = {...props, wrappedComponent};

        return React.createElement(ModelProvider, mpProps);
    };
}

export const ModelProvider = observer(function MP(props: ModelProviderProps) {
    const {cqForceReload, cqPath} = props;
    // state is not used as it comes from modelStore. The trigger is needed here.
    const [, setState] = React.useState<ModelProviderState>({
        model: null
    });

    const modelStore = useModelStore();

    const onLoad = React.useCallback(
        (data: TransferModel) => {
            setState({model: modelToProps(data, cqPath)});
        },
        [setState, cqPath]
    );

    const updateData = React.useCallback(() => {
        ModelManager.getData({
            path: cqPath,
            forceReload: cqForceReload || false
        }).then(onLoad);
    }, [onLoad, cqPath, cqForceReload]);

    React.useEffect(() => {
        ModelManager.addListener(props.cqPath, updateData);

        return () => {
            ModelManager.removeListener(props.cqPath, updateData);
        };
    });

    // model store will be observed to check if custom data (e.g. merged data) has changed.
    const model = modelStore.getData(cqPath);
    const newModel = modelToProps(model, cqPath);
    const newProps = mergeProps(newModel, props);

    return React.createElement(
        props.wrappedComponent,
        newProps as React.Attributes
    );
});
